enum ReportAggregation {
  Summary = 'Summary',
  Hourly = 'Hourly',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
  HourOfDay = 'HourOfDay',
  DayOfWeek = 'DayOfWeek',
  WeeklyStartingMonday = 'WeeklyStartingMonday',
}

enum ReportAggregationColumn {
  GregorianDate = 'GregorianDate',
  Week = 'Week',
  Month = 'Month',
  Year = 'Year',
  HourOfDay = 'HourOfDay',
  DayOfWeek = 'DayOfWeek',
  Quarter = 'Quarter',
  WeekStartingMonday = 'WeekStartingMonday',
}

export interface PilotSet {
  isAllowed: (permissions: any, isCustomerScope: boolean, isColumnChooserRenderer: boolean) => boolean;
  columns: string[];
}

export enum SelectOption {
  mandatory = 'mandatory',
}

export type TimeAggregationEnum = string;

export interface Option {
  key: string;
  name: string;
  type: string;
  helpTopic?: string;
  forbiddenTimeGrains?: TimeAggregationEnum[];
  selectOption?: SelectOption;
}

export interface CategoryDataModel {
  name: string;
  type: string;
  options: Option[];
}

export const mapOfReportAggregationEnumAndColumn = {
  Summary: undefined,
  Hourly: undefined,
  Daily: 'GregorianDate',
  Weekly: 'Week',
  Monthly: 'Month',
  Yearly: 'Year',
  HourOfDay: 'HourOfDay',
  DayOfWeek: 'DayOfWeek',
  WeeklyStartingMonday: 'WeekStartingMonday',
};

/**
 * ATTENTION! This is not a bijection.
 * This behavior is guessed by myself: If user chooses more detaied option, the returned value is more detailed
 * which means if `Year` and `Date` is both selected, the equivalent report aggregation is `Daily` rather than `Yearly`
 */
export function convertColumnsToOldUIReportAggregation(columns: Set<string> | string[]) {
  if (Array.isArray(columns)) {
    // eslint-disable-next-line no-param-reassign
    columns = new Set(columns);
  }
  switch (true) {
    case columns.has(ReportAggregationColumn.HourOfDay) && columns.has(ReportAggregationColumn.GregorianDate):
      return ReportAggregation.Hourly;
    case columns.has(ReportAggregationColumn.HourOfDay):
      return ReportAggregation.HourOfDay;
    case columns.has(ReportAggregationColumn.GregorianDate):
      return ReportAggregation.Daily;
    case columns.has(ReportAggregationColumn.DayOfWeek):
      return ReportAggregation.DayOfWeek;
    case columns.has(ReportAggregationColumn.Week):
      return ReportAggregation.Weekly;
    case columns.has(ReportAggregationColumn.WeekStartingMonday):
      return ReportAggregation.WeeklyStartingMonday;
    case columns.has(ReportAggregationColumn.Month):
      return ReportAggregation.Monthly;
    case columns.has(ReportAggregationColumn.Year):
      return ReportAggregation.Yearly;
    default:
      return ReportAggregation.Summary;
  }
}

/**
 * @param columns the columns selected by the user
 * @returns ATTENTION: the report aggregation is used internal by MT rather than used in the old UI.
 */
export function convertColumnsToReportAggregation(columns: Set<string> | string[]) {
  if (Array.isArray(columns)) {
    // eslint-disable-next-line no-param-reassign
    columns = new Set(columns);
  }
  let reportAggregation: ReportAggregation = ReportAggregation.Summary;
  if (columns.has(ReportAggregationColumn.HourOfDay)) {
    if (hasDateTypeColumns(columns) || columns.has(ReportAggregationColumn.HourOfDay)) {
      reportAggregation = ReportAggregation.Hourly;
    } else {
      reportAggregation = ReportAggregation.HourOfDay;
    }
  } else if (columns.has(ReportAggregationColumn.DayOfWeek)) {
    if (hasDateTypeColumns(columns)) {
      reportAggregation = ReportAggregation.Daily;
    } else {
      reportAggregation = ReportAggregation.DayOfWeek;
    }
  } else if (hasDateTypeColumns(columns)) {
    reportAggregation = ReportAggregation.Daily;
  }
  return reportAggregation;
}

function hasDateTypeColumns(reportColumns: Set<string>) {
  return reportColumns.has(ReportAggregationColumn.GregorianDate)
    || reportColumns.has(ReportAggregationColumn.Week)
    || reportColumns.has(ReportAggregationColumn.WeekStartingMonday)
    || reportColumns.has(ReportAggregationColumn.Month)
    || reportColumns.has(ReportAggregationColumn.Quarter)
    || reportColumns.has(ReportAggregationColumn.Year);
}

// Some columns could not be seen by user who does not have permissions.
export function getRejectedColumns(pilotSets: PilotSet[], permissions: any, isCustomerScope: boolean = false, isColumnChooserRenderer: boolean = false) {
  const rejectedColumns = new Set<string>();
  pilotSets.forEach(({ columns, isAllowed }) => {
    if (!isAllowed(permissions, isCustomerScope, isColumnChooserRenderer)) {
      columns.forEach(column => rejectedColumns.add(column));
    }
  });
  return rejectedColumns;
}

function isAvailableByTimeGrain(timeGrain: TimeAggregationEnum, option: Option) {
  return !(option.forbiddenTimeGrains && option.forbiddenTimeGrains.includes(timeGrain));
}

function filterCategoriesByTimeGrain(categories: CategoryDataModel[], timeGrain: TimeAggregationEnum) {
  return categories.map(category => ({
    ...category,
    options: category.options.filter(option => isAvailableByTimeGrain(timeGrain, option)),
  }));
}

function filterCategoriesByRejectedColumns(categories: CategoryDataModel[], rejectedColumns: Set<string>) {
  return categories.map(category => ({
    ...category,
    options: category.options.filter(option => !rejectedColumns.has(option.key)),
  }));
}

export function filterCategories(categories: CategoryDataModel[], by: { rejectedColumns?: Set<string>, timeGrain?: TimeAggregationEnum }) {
  let res = categories;
  if (by.timeGrain) {
    res = filterCategoriesByTimeGrain(categories, by.timeGrain);
  }
  if (by.rejectedColumns) {
    res = filterCategoriesByRejectedColumns(res, by.rejectedColumns);
  }
  return res;
}

export function getAvaliableColumnSet(categories: CategoryDataModel[]) {
  const avaliableColumnSet = new Set<string>();
  categories.forEach((category) => {
    category.options.forEach(option => avaliableColumnSet.add(option.key));
  });
  return avaliableColumnSet;
}

export function filterColumnByAvaliableColumns(columns: string[], avaliableColumnSet: Set<string>) {
  return columns.filter(column => avaliableColumnSet.has(column));
}

export function addMandatoryToSelectedByCategories(columns: string[], categories: CategoryDataModel[]) {
  const selectedSet = new Set(columns);
  const newSelected = [...columns];

  categories.forEach(category => category.options.forEach((option) => {
    if (option && option.selectOption === SelectOption.mandatory && !selectedSet.has(option.key)) {
      newSelected.unshift(option.key);
    }
  }));
  return newSelected;
}

export function addMandatoryToSelectedByMandateColumnSet(columns: string[], MandateColumnSet: string[][], avaliableColumns: Set<string>) {
  const columnSet = new Set(columns);
  const newColumns = [...columns];
  MandateColumnSet.forEach((mandateColumns) => {
    const isMandatoryColumnAllMissing = !mandateColumns.some(column => columnSet.has(column));
    if (isMandatoryColumnAllMissing) {
      const firstAvaliableColumn = mandateColumns.find(column => avaliableColumns.has(column));
      // TODO: should we throw an error if `firstAvaliableColumn` is not found?
      if (firstAvaliableColumn) {
        newColumns.push(firstAvaliableColumn);
      }
    }
  });
  return newColumns;
}

/**
 * If report does not support "summary" report aggregation, it must select one time column
 */
export function addDefaultTimeColumn(columns: string[], allowedReportAggregationEnums: string[]) {
  const newSelected = [...columns];
  const allowedReportAggregationColumnSet = new Set(allowedReportAggregationEnums.map(reportAggregationEnum => mapOfReportAggregationEnumAndColumn[reportAggregationEnum]));
  if (!allowedReportAggregationEnums.includes('Summary')) {
    if (!newSelected.some(column => allowedReportAggregationColumnSet.has(column))) {
      const defaultAggregationEnum = allowedReportAggregationEnums[0];
      newSelected.push(mapOfReportAggregationEnumAndColumn[defaultAggregationEnum]);
    }
  }
  return newSelected;
}

export function getCanonicalCategoriesAndColumns({
  columns, reportSchema, permissions, history,
}) {
  let canonicalColumns;
  if (columns.length === 0) {
    canonicalColumns = reportSchema.Columns.default;
  } else {
    canonicalColumns = columns;
  }

  const { aid: accountId } = history.query;

  const rejectedColumns = getRejectedColumns(reportSchema.Columns.piloted, permissions, !(accountId > 0));
  const defaultRejectedColumns = getRejectedColumns(reportSchema.Columns.pilotDefault, permissions);

  canonicalColumns = addMandatoryToSelectedByCategories(canonicalColumns, reportSchema.Columns.categories);
  canonicalColumns = addDefaultTimeColumn(canonicalColumns, reportSchema.ReportAggregation.enum);
  const equivalentReportAggregation = convertColumnsToReportAggregation(new Set(canonicalColumns));
  const categories = filterCategories(reportSchema.Columns.categories, { rejectedColumns, timeGrain: equivalentReportAggregation });
  const avaliableColumns = getAvaliableColumnSet(categories);
  canonicalColumns = addMandatoryToSelectedByMandateColumnSet(canonicalColumns, reportSchema.MandateColumnSet ?? [], avaliableColumns);
  canonicalColumns = filterColumnByAvaliableColumns(canonicalColumns, avaliableColumns);

  if (columns.length === 0) {
    canonicalColumns = canonicalColumns.filter(column => !defaultRejectedColumns.has(column));
  }

  return { categories, columns: canonicalColumns };
}
